import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Box, IconButton, Typography, Button } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import zapIconActive from '../../../assets/images/zapIconActive.svg';
import StorefrontImage from '../../../assets/images/storefront.svg';
import LinkinBioImage from '../../../assets/images/link-in-bio.svg';

/**
 * "What is Zap?" — the hub-level explainer.
 *
 * The Automations screen already has its own "How it works?" / "How to use Zap"
 * helpers, but the Zap hub (Automations + Storefront + Link in Bio) had nothing
 * telling a first-time creator what Zap actually is. This modal introduces Zap
 * as a whole and then explains each tool section by section in the same order
 * the hub cards appear in.
 *
 * Copy lives in ZAP_SECTIONS so web and the mobile app (clanconnect_mobile:
 * modules/influencer/components/zap/WhatIsZapModal.tsx) stay word-for-word
 * identical — please update both together.
 */

export interface ZapSection {
    id: string;
    title: string;
    subtitle: string;
    /** One-line answer to "what is this?" */
    what: string;
    /** "How to use it" — short, ordered steps. */
    steps: string[];
    /** Who/what it is best for. */
    bestFor: string;
    icon: string;
    iconSize: number;
}

export const ZAP_SECTIONS: ZapSection[] = [
    {
        id: 'automations',
        title: 'Zap Automations',
        subtitle: 'Instagram DM',
        what: 'Turns a comment on your Instagram content into an instant DM. When a follower comments a keyword you choose on your Post, Reel, Story or Live, Zap replies to the comment and sends them a DM — a message, a link, or a rich Zap Link product card.',
        steps: [
            'Connect your Instagram account (one-time).',
            'Pick what to automate — Post/Reel, Story or Live.',
            'Add trigger keywords, e.g. "link", "price", "details".',
            'Write the DM, or attach a Zap Link with your products.',
            'Create the automation — it starts working right away.',
        ],
        bestFor: 'Never missing a "where can I buy this?" comment, even while you sleep.',
        icon: zapIconActive,
        iconSize: 26,
    },
    {
        id: 'storefront',
        title: 'Zap Storefront',
        subtitle: 'Your Shop',
        what: 'Your own shop page on ClanConnect. Add the products you recommend — your own, a brand\'s, or affiliate picks — and share one link that shows them all in a clean, branded layout.',
        steps: [
            'Set up your store — name, handle, logo and banner.',
            'Add products with an image, price and buy link.',
            'Share your storefront link in your bio, DMs or Zap replies.',
            'Track views and clicks in Storefront analytics.',
        ],
        bestFor: 'Selling and recommending products without building a website.',
        icon: StorefrontImage,
        iconSize: 28,
    },
    {
        id: 'link-in-bio',
        title: 'Zap - Link in Bio',
        subtitle: 'Bio Page',
        what: 'One beautiful page behind the single link Instagram gives you. Stack blocks — links, social profiles, videos, your storefront, collaborations — and rearrange them whenever you like.',
        steps: [
            'Choose your page link (e.g. clanconnect.in/bio/yourname).',
            'Add your photo, bio and social profiles.',
            'Add blocks for links, videos, products or your storefront.',
            'Publish and paste the link into your Instagram bio.',
            'Watch views and clicks in Bio analytics.',
        ],
        bestFor: 'Sending followers to everything you do from one place.',
        icon: LinkinBioImage,
        iconSize: 28,
    },
];

interface Props {
    open: boolean;
    onClose: () => void;
    /** Optional: jump straight to a tool's section when opened from a card. */
    initialSection?: string;
}

const ACCENT = '#7C3AED';

const WhatIsZapModal: React.FC<Props> = ({ open, onClose, initialSection }) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [activeId, setActiveId] = useState<string>('overview');

    const scrollToSection = useCallback((id: string) => {
        const el = scrollRef.current;
        if (!el) return;
        const target = el.querySelector<HTMLElement>(`[data-section="${id}"]`);
        if (target) el.scrollTo({ top: Math.max(target.offsetTop - 12, 0), behavior: 'smooth' });
    }, []);

    // Highlight the chip of the section currently under the reference line.
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const line = el.scrollTop + 80;
        let current = 'overview';
        el.querySelectorAll<HTMLElement>('[data-section]').forEach((s) => {
            if (s.offsetTop <= line) current = s.dataset.section || current;
        });
        setActiveId(current);
    }, []);

    // Reset (or deep-link) the scroll position every time it opens.
    useEffect(() => {
        if (!open) return;
        setActiveId(initialSection || 'overview');
        requestAnimationFrame(() => {
            if (initialSection) scrollToSection(initialSection);
            else scrollRef.current?.scrollTo({ top: 0 });
        });
    }, [open, initialSection, scrollToSection]);

    return (
        <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 0, sm: 2 } }}>
            <Box sx={{
                bgcolor: '#fff',
                width: '100%',
                maxWidth: 720,
                height: { xs: '100%', sm: 'auto' },
                maxHeight: { xs: '100%', sm: '90vh' },
                borderRadius: { xs: 0, sm: '18px' },
                display: 'flex',
                flexDirection: 'column',
                outline: 'none',
                overflow: 'hidden',
            }}>
                {/* Sticky header: title, close, section chips */}
                <Box sx={{ borderBottom: '1px solid #ececf0', px: 2, pt: 2, pb: 1.25, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: '50%', border: '1px solid #ececf0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <img src={zapIconActive} alt="" width={20} height={20} />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111' }}>
                            What is Zap?
                        </Typography>
                        <IconButton onClick={onClose} aria-label="Close" sx={{ ml: 'auto' }} size="small">
                            <CloseRounded sx={{ fontSize: 20, color: '#374151' }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 1.25, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
                        {[{ id: 'overview', label: 'Overview' }, ...ZAP_SECTIONS.map((s) => ({ id: s.id, label: s.title.replace('Zap - ', '').replace('Zap ', '') }))].map((chip) => {
                            const active = activeId === chip.id;
                            return (
                                <Box
                                    key={chip.id}
                                    onClick={() => scrollToSection(chip.id)}
                                    sx={{
                                        flexShrink: 0, cursor: 'pointer', borderRadius: '999px',
                                        px: 1.5, py: 0.5, fontSize: 12.5, fontWeight: 600,
                                        border: '1px solid', transition: 'all .15s ease',
                                        borderColor: active ? ACCENT : '#ececf0',
                                        bgcolor: active ? '#f5f3ff' : '#fff',
                                        color: active ? ACCENT : '#6b7280',
                                    }}
                                >
                                    {chip.label}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Body */}
                <Box ref={scrollRef} onScroll={handleScroll} sx={{ overflowY: 'auto', px: 2, py: 2, flex: 1 }}>
                    {/* Overview */}
                    <Box data-section="overview" sx={{
                        borderRadius: '16px', p: 2.5,
                        background: 'linear-gradient(135deg, #f5f3ff 0%, #fce7f3 100%)',
                    }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: ACCENT }}>
                            Creator tools
                        </Typography>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#111', mt: 0.5 }}>
                            Zap in one line
                        </Typography>
                        <Typography sx={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.6, mt: 1 }}>
                            Zap is your creator toolkit inside ClanConnect. It turns the audience you already
                            have on Instagram into replies, clicks and sales — without you answering every
                            comment or building a website. Zap has three tools, and you can use one or all three.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {ZAP_SECTIONS.map((s, i) => (
                                <Box key={s.id} onClick={() => scrollToSection(s.id)} sx={{
                                    cursor: 'pointer', bgcolor: 'rgba(255,255,255,0.8)', borderRadius: '10px',
                                    px: 1.5, py: 1, fontSize: 12.5, fontWeight: 600, color: '#111',
                                }}>
                                    {i + 1}. {s.title}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* One section per tool */}
                    {ZAP_SECTIONS.map((section) => (
                        <Box
                            key={section.id}
                            data-section={section.id}
                            sx={{ border: '1px solid #ececf0', borderRadius: '16px', p: 2.5, mt: 2 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 48, height: 48, borderRadius: '14px', border: '1px solid #ececf0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <img src={section.icon} alt="" width={section.iconSize} height={section.iconSize} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111', lineHeight: 1.2 }}>
                                        {section.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', mt: 0.25 }}>
                                        {section.subtitle}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6, mt: 1.5 }}>
                                {section.what}
                            </Typography>

                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9ca3af', mt: 2 }}>
                                How to use it
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {section.steps.map((step, i) => (
                                    <Box key={step} sx={{ display: 'flex', gap: 1.25, mb: 0.75 }}>
                                        <Box sx={{
                                            width: 20, height: 20, borderRadius: '50%', bgcolor: '#f5f3ff',
                                            color: ACCENT, fontSize: 11.5, fontWeight: 700, flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '2px',
                                        }}>
                                            {i + 1}
                                        </Box>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.5 }}>
                                            {step}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ bgcolor: '#f9fafb', borderRadius: '10px', px: 1.5, py: 1.25, mt: 1.5 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.5 }}>
                                    <strong style={{ color: '#111' }}>Best for: </strong>{section.bestFor}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    {/* How they work together */}
                    <Box sx={{ border: '1px dashed #ddd6fe', bgcolor: '#faf5ff', borderRadius: '16px', p: 2.5, mt: 2 }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111' }}>
                            Better together
                        </Typography>
                        <Typography sx={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.6, mt: 0.75 }}>
                            Build your Storefront, put it on your Link in Bio page, and let Automations DM that
                            link to everyone who comments. One setup, and every comment turns into a visit to
                            your shop.
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: '#6b7280', mt: 1.5 }}>
                            Storefront and Link in Bio are available on select plans — a card marked
                            {' "Upgrade" '} opens your subscription options.
                        </Typography>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ borderTop: '1px solid #ececf0', px: 2, py: 1.5, flexShrink: 0 }}>
                    <Button
                        fullWidth
                        onClick={onClose}
                        sx={{
                            bgcolor: '#111', color: '#fff', borderRadius: '999px', py: 1,
                            textTransform: 'none', fontWeight: 700,
                            '&:hover': { bgcolor: '#000' },
                        }}
                    >
                        Got it
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default WhatIsZapModal;
